import React, { useState, useEffect, useCallback} from 'react';
import { useSearchParams } from 'react-router-dom';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import toast from 'react-hot-toast';
import { Plus } from 'lucide-react';
import { RecordActions } from '../components/common/RecordActions';
import { RecordViewDialog } from '../components/common/RecordViewDialog';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const VendorMaster = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('vendor_master');
  const [viewItem, setViewItem] = useState(null);
  const [searchParams] = useSearchParams();
  const tabFromUrl = searchParams.get('tab') || 'account-groups';
  const [activeTab, setActiveTab] = useState(tabFromUrl);
  
  const [accountGroups, setAccountGroups] = useState([]);
  const [numberRanges, setNumberRanges] = useState([]);
  const [partnerFunctions, setPartnerFunctions] = useState([]);
  const [configurations, setConfigurations] = useState([]);
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // For configuration form
  const [selectedAccountGroup, setSelectedAccountGroup] = useState('');
  const [selectedNumberRange, setSelectedNumberRange] = useState('');
  const [selectedPartnerFunctions, setSelectedPartnerFunctions] = useState([]);
  
  // For number range form
  const [selectedRangeType, setSelectedRangeType] = useState('INTERNAL');

  useEffect(() => {
    setActiveTab(tabFromUrl);
  }, [tabFromUrl]);


  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      if (activeTab === 'account-groups') {
        const response = await axios.get(`${BACKEND_URL}/api/vendor-master/account-groups`);
        setAccountGroups(response.data || []);
      } else if (activeTab === 'number-ranges') {
        const response = await axios.get(`${BACKEND_URL}/api/vendor-master/number-ranges`);
        setNumberRanges(response.data || []);
      } else if (activeTab === 'partner-functions') {
        const response = await axios.get(`${BACKEND_URL}/api/vendor-master/partner-functions`);
        setPartnerFunctions(response.data || []);
      } else if (activeTab === 'configurations') {
        const [configsRes, groupsRes, rangesRes, functionsRes] = await Promise.all([
          axios.get(`${BACKEND_URL}/api/vendor-master/account-group-configs`),
          axios.get(`${BACKEND_URL}/api/vendor-master/account-groups`),
          axios.get(`${BACKEND_URL}/api/vendor-master/number-ranges`),
          axios.get(`${BACKEND_URL}/api/vendor-master/partner-functions`)
        ]);
        setConfigurations(configsRes.data || []);
        setAccountGroups(groupsRes.data || []);
        setNumberRanges(rangesRes.data || []);
        setPartnerFunctions(functionsRes.data || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load data');
    } finally {
      setIsLoading(false);
    }
  }, [activeTab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Handle configuration separately
    if (activeTab === 'configurations') {
      return handleConfigSubmit();
    }
    
    const formData = new FormData(e.currentTarget);
    const data = {};
    formData.forEach((value, key) => {
      // Handle numeric fields
      if (key === 'from_number' || key === 'to_number') {
        data[key] = value ? parseInt(value, 10) : null;
      } else if (key === 'unique') {
        // Handle checkbox - FormData returns "on" for checked checkboxes
        data[key] = value === 'on' ? true : false;
      } else {
        data[key] = value === '' ? null : value;
      }
    });
    
    // If unique checkbox was not in form (unchecked), set it to false
    if (activeTab === 'partner-functions' && !formData.has('unique')) {
      data.unique = false;
    }

    // Validation for number ranges
    if (activeTab === 'number-ranges') {
      if (!data.range_type) {
        toast.error('Please select Number Assignment Type');
        return;
      }
      if (data.range_type === 'INTERNAL') {
        if (!data.from_number || !data.to_number) {
          toast.error('From Number and To Number are required for Internal ranges');
          return;
        }
        if (data.to_number <= data.from_number) {
          toast.error('To Number must be greater than From Number');
          return;
        }
      }
      if (data.range_type === 'EXTERNAL') {
        if (!data.prefix) {
          toast.error('Prefix is mandatory for External ranges to prevent overlapping with Internal numbers');
          return;
        }
        if (data.prefix.length !== 2) {
          toast.error('Prefix must be exactly 2 characters for External ranges');
          return;
        }
      }
    }

    try {
      if (editingItem) {
        await updateItem(data);
      } else {
        await createItem(data);
      }
      setIsDialogOpen(false);
      setEditingItem(null);
      loadData();
      toast.success(editingItem ? 'Updated successfully' : 'Created successfully');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (Array.isArray(detail)) {
        const errorMessages = detail.map(err => `${err.loc.join(' > ')}: ${err.msg}`).join('; ');
        toast.error(`Validation Error: ${errorMessages}`);
      } else if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Operation failed. Please check your inputs.');
      }
    }
  };

  const handleConfigSubmit = async () => {
    // Validation
    if (!selectedAccountGroup) {
      toast.error('Please select an Account Group');
      return;
    }
    if (!selectedNumberRange) {
      toast.error('Please select a Number Range');
      return;
    }
    if (selectedPartnerFunctions.length === 0) {
      toast.error('Please select at least one Partner Function');
      return;
    }

    const data = {
      account_group_id: selectedAccountGroup,
      number_range_id: selectedNumberRange,
      partner_function_ids: selectedPartnerFunctions
    };

    try {
      if (editingItem) {
        await axios.put(`${BACKEND_URL}/api/vendor-master/account-group-configs/${selectedAccountGroup}`, {
          number_range_id: selectedNumberRange,
          partner_function_ids: selectedPartnerFunctions
        });
      } else {
        await axios.post(`${BACKEND_URL}/api/vendor-master/account-group-configs`, data);
      }
      setIsDialogOpen(false);
      setEditingItem(null);
      setSelectedAccountGroup('');
      setSelectedNumberRange('');
      setSelectedPartnerFunctions([]);
      loadData();
      toast.success(editingItem ? 'Configuration updated successfully' : 'Configuration created successfully');
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error('Operation failed. Please check your inputs.');
      }
    }
  };

  const createItem = async (data) => {
    if (activeTab === 'account-groups') {
      await axios.post(`${BACKEND_URL}/api/vendor-master/account-groups`, data);
    } else if (activeTab === 'number-ranges') {
      await axios.post(`${BACKEND_URL}/api/vendor-master/number-ranges`, data);
    } else if (activeTab === 'partner-functions') {
      await axios.post(`${BACKEND_URL}/api/vendor-master/partner-functions`, data);
    }
  };

  const updateItem = async (data) => {
    if (activeTab === 'account-groups') {
      await axios.put(`${BACKEND_URL}/api/vendor-master/account-groups/${editingItem.account_group_id}`, data);
    } else if (activeTab === 'number-ranges') {
      await axios.put(`${BACKEND_URL}/api/vendor-master/number-ranges/${editingItem.number_range_id}`, data);
    } else if (activeTab === 'partner-functions') {
      await axios.put(`${BACKEND_URL}/api/vendor-master/partner-functions/${editingItem.partner_function_id}`, data);
    }
  };


  const getCurrentData = () => {
    if (activeTab === 'account-groups') return accountGroups;
    if (activeTab === 'number-ranges') return numberRanges;
    if (activeTab === 'partner-functions') return partnerFunctions;
    if (activeTab === 'configurations') return configurations;
    return [];
  };

  const getIdField = () => {
    if (activeTab === 'account-groups') return 'account_group_id';
    if (activeTab === 'number-ranges') return 'number_range_id';
    if (activeTab === 'partner-functions') return 'partner_function_id';
    if (activeTab === 'configurations') return 'account_group_id';
    return 'id';
  };

  const getDialogTitle = () => {
    if (activeTab === 'account-groups') return editingItem ? 'Edit Account Group' : 'Create Account Group';
    if (activeTab === 'number-ranges') return editingItem ? 'Edit Number Range' : 'Create Number Range';
    if (activeTab === 'partner-functions') return editingItem ? 'Edit Partner Function' : 'Create Partner Function';
    if (activeTab === 'configurations') return editingItem ? 'Edit Account Group Configuration' : 'Create Account Group Configuration';
    return '';
  };

  const getDisplayText = (item) => {
    if (activeTab === 'account-groups') {
      return (
        <div>
          <div className="font-medium">{item.description || '-'}</div>
        </div>
      );
    }
    if (activeTab === 'number-ranges') {
      const isExternal = item.range_type === 'EXTERNAL';
      
      return (
        <div>
          {isExternal ? (
            <>
              <div className="font-medium flex items-center gap-2">
                <span className="px-2 py-0.5 text-xs font-semibold rounded bg-orange-100 text-orange-700">
                  External (Manual Entry)
                </span>
              </div>
              <div className="text-xs text-gray-600 mt-1">
                Vendor numbers are entered manually by users
              </div>
            </>
          ) : (
            <>
              <div className="font-medium">Range: {item.from_number || 0} - {item.to_number || 0}</div>
              <div className="text-xs text-gray-500 mt-0.5 flex items-center gap-2">
                <span className="px-2 py-0.5 text-xs font-semibold rounded bg-green-100 text-green-700">
                  Internal (Auto-generated)
                </span>
                <span>Current: {item.current_number ?? (item.from_number ? item.from_number - 1 : 0)}</span>
              </div>
            </>
          )}
        </div>
      );
    }
    if (activeTab === 'partner-functions') {
      return (
        <div>
          <div className="font-medium flex items-center gap-2">
            {item.name || '-'}
            {item.unique && (
              <span className="px-2 py-0.5 text-xs font-semibold rounded bg-amber-100 text-amber-700">
                UNIQUE
              </span>
            )}
          </div>
          {item.description && <div className="text-xs text-gray-500 mt-0.5">{item.description}</div>}
          {item.unique && (
            <div className="text-xs text-amber-600 mt-1 font-medium">
              ⚠️ One-time use only - can only be assigned to one vendor
            </div>
          )}
        </div>
      );
    }
    if (activeTab === 'configurations') {
      const accountGroup = accountGroups.find(ag => ag.account_group_id === item.account_group_id);
      const numberRange = numberRanges.find(nr => nr.number_range_id === item.number_range_id);
      const partnerFuncs = partnerFunctions.filter(pf => item.partner_function_ids?.includes(pf.partner_function_id));
      
      return (
        <div>
          <div className="font-medium mb-1">
            Account Group: <span className="text-orange-600">{accountGroup?.description || item.account_group_id}</span>
          </div>
          <div className="text-sm text-gray-600">
            <span className="font-medium">Number Range:</span> {item.number_range_id}
            {numberRange && (() => {
              const isExternal = numberRange.range_type === 'EXTERNAL';
              if (isExternal) {
                return ' (External - Manual Entry)';
              } else {
                return ` (${numberRange.from_number || 0}-${numberRange.to_number || 0}, Internal)`;
              }
            })()}
          </div>
          <div className="text-sm text-gray-600 mt-1">
            <span className="font-medium">Partner Functions:</span> {partnerFuncs.map(pf => pf.name).join(', ') || 'None'}
          </div>
        </div>
      );
    }
    return '-';
  };

  const openDialog = (item = null) => {
    setEditingItem(item);
    if (activeTab === 'configurations') {
      if (item) {
        setSelectedAccountGroup(item.account_group_id);
        setSelectedNumberRange(item.number_range_id);
        setSelectedPartnerFunctions(item.partner_function_ids || []);
      } else {
        setSelectedAccountGroup('');
        setSelectedNumberRange('');
        setSelectedPartnerFunctions([]);
      }
    }
    if (activeTab === 'number-ranges') {
      // Set initial range type based on editing item or default to INTERNAL
      setSelectedRangeType(item?.range_type || 'INTERNAL');
    }
    setIsDialogOpen(true);
  };

  const togglePartnerFunction = (pfId) => {
    setSelectedPartnerFunctions(prev => {
      if (prev.includes(pfId)) {
        return prev.filter(id => id !== pfId);
      } else {
        return [...prev, pfId];
      }
    });
  };

  const getFormFields = () => {
    if (activeTab === 'account-groups') {
      return (
        <>
          <div>
            <Label>Account Group ID *</Label>
            <Input 
              name="account_group_id" 
              defaultValue={editingItem?.account_group_id || ''} 
              required 
              disabled={!!editingItem}
              placeholder="e.g., DOME, FRGN"
              maxLength={10}
              style={{ textTransform: 'uppercase' }}
            />
            <p className="text-xs text-gray-500 mt-1">Unique identifier (cannot be changed after creation)</p>
          </div>
          <div>
            <Label>Description *</Label>
            <Input 
              name="description" 
              defaultValue={editingItem?.description || ''} 
              required 
              placeholder="e.g., Domestic Vendors, Foreign Vendors"
              maxLength={255}
            />
          </div>
        </>
      );
    }

    if (activeTab === 'number-ranges') {
      return (
        <>
          <div>
            <Label>Number Range ID *</Label>
            <Input 
              name="number_range_id" 
              defaultValue={editingItem?.number_range_id || ''} 
              required 
              disabled={!!editingItem}
              placeholder="e.g., V01, V02"
              maxLength={10}
            />
            <p className="text-xs text-gray-500 mt-1">Unique identifier (cannot be changed after creation)</p>
          </div>
          <div>
            <Label>Description *</Label>
            <Input 
              name="description" 
              defaultValue={editingItem?.description || ''} 
              required 
              placeholder="e.g., Domestic Vendors, International Vendors"
              maxLength={100}
            />
          </div>
          {selectedRangeType === 'EXTERNAL' && (
            <div>
              <Label>Prefix *</Label>
              <Input 
                name="prefix" 
                defaultValue={editingItem?.prefix || ''} 
                placeholder="e.g., VE, VD"
                minLength={2}
                maxLength={2}
                required
                style={{ textTransform: 'uppercase' }}
              />
              <p className="text-xs text-gray-500 mt-1">
                Exactly 2 characters required for EXTERNAL ranges to prevent overlap with INTERNAL numbers
              </p>
            </div>
          )}
          {selectedRangeType === 'INTERNAL' && (
            <div>
              <Label>Prefix (Optional)</Label>
              <Input 
                name="prefix" 
                defaultValue={editingItem?.prefix || ''} 
                placeholder="e.g., V, VEND (optional)"
                maxLength={2}
                style={{ textTransform: 'uppercase' }}
              />
              <p className="text-xs text-gray-500 mt-1">Optional prefix for vendor codes</p>
            </div>
          )}
          <div>
            <Label>Number Assignment Type *</Label>
            <div className="flex gap-4 mt-2">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="range_type"
                  value="INTERNAL"
                  checked={selectedRangeType === 'INTERNAL'}
                  onChange={(e) => setSelectedRangeType(e.target.value)}
                  className="w-4 h-4"
                />
                <span className="text-sm">Internal (Auto-generated)</span>
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="range_type"
                  value="EXTERNAL"
                  checked={selectedRangeType === 'EXTERNAL'}
                  onChange={(e) => setSelectedRangeType(e.target.value)}
                  className="w-4 h-4"
                />
                <span className="text-sm">External (Manual)</span>
              </label>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Internal: System auto-generates vendor numbers | External: User provides vendor numbers
            </p>
          </div>
          {selectedRangeType === 'INTERNAL' && (
            <>
              <div>
                <Label>From Number *</Label>
                <Input 
                  type="number" 
                  name="from_number" 
                  defaultValue={editingItem?.from_number || ''} 
                  required 
                  min={0}
                  placeholder="e.g., 1, 1000"
                />
              </div>
              <div>
                <Label>To Number *</Label>
                <Input 
                  type="number" 
                  name="to_number" 
                  defaultValue={editingItem?.to_number || ''} 
                  required 
                  min={0}
                  placeholder="e.g., 999, 9999"
                />
                <p className="text-xs text-gray-500 mt-1">Must be greater than From Number</p>
              </div>
            </>
          )}
          <div className="bg-orange-50 border border-orange-200 rounded-lg p-3">
            <p className="text-sm text-orange-800">
              <strong>Note:</strong> Total vendor number length is fixed at <strong>10 digits</strong>
              {selectedRangeType === 'EXTERNAL' && ' (2-char prefix + 8-digit number)'}
              {selectedRangeType === 'INTERNAL' && ' (with optional prefix)'}
            </p>
          </div>
        </>
      );
    }

    if (activeTab === 'partner-functions') {
      return (
        <>
          <div>
            <Label>Partner Function ID *</Label>
            <Input 
              name="partner_function_id" 
              defaultValue={editingItem?.partner_function_id || ''} 
              required 
              disabled={!!editingItem}
              placeholder="e.g., SP, BP, PY"
              maxLength={10}
              style={{ textTransform: 'uppercase' }}
            />
            <p className="text-xs text-gray-500 mt-1">Unique identifier (cannot be changed after creation)</p>
          </div>
          <div>
            <Label>Name *</Label>
            <Input 
              name="name" 
              defaultValue={editingItem?.name || ''} 
              required 
              placeholder="e.g., Sold-to Party, Ship-to Party"
              maxLength={100}
            />
          </div>
          <div>
            <Label>Description</Label>
            <Input 
              name="description" 
              defaultValue={editingItem?.description || ''} 
              placeholder="Optional detailed description"
              maxLength={255}
            />
          </div>
          <div className="flex items-start space-x-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <input 
              type="checkbox" 
              name="unique" 
              id="unique-checkbox"
              defaultChecked={editingItem?.unique || false}
              className="mt-1 w-4 h-4 text-amber-600 border-gray-300 rounded focus:ring-amber-500"
            />
            <div className="flex-1">
              <label htmlFor="unique-checkbox" className="text-sm font-medium text-amber-900 cursor-pointer">
                Mark as Unique (One-time Use)
              </label>
              <p className="text-xs text-amber-700 mt-1">
                If checked, this partner function can only be assigned to <strong>one vendor</strong>. Once assigned, it becomes consumed and cannot be used by other vendors.
              </p>
            </div>
          </div>
        </>
      );
    }

    if (activeTab === 'configurations') {
      return (
        <>
          <div>
            <Label>Account Group *</Label>
            <select
              value={selectedAccountGroup}
              onChange={(e) => setSelectedAccountGroup(e.target.value)}
              disabled={!!editingItem}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              required
            >
              <option value="">Select Account Group</option>
              {accountGroups.map(ag => (
                <option key={ag.account_group_id} value={ag.account_group_id}>
                  {ag.account_group_id} - {ag.description}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">Cannot be changed after creation</p>
          </div>

          <div>
            <Label>Number Range *</Label>
            <select
              value={selectedNumberRange}
              onChange={(e) => setSelectedNumberRange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
              required
            >
              <option value="">Select Number Range</option>
              {numberRanges.map(nr => {
                const isExternal = nr.range_type === 'EXTERNAL';
                const rangeText = isExternal 
                  ? 'External (Manual Entry)' 
                  : `${nr.from_number}-${nr.to_number}, Internal`;
                return (
                  <option key={nr.number_range_id} value={nr.number_range_id}>
                    {nr.number_range_id} ({rangeText})
                  </option>
                );
              })}
            </select>
          </div>

          <div>
            <Label>Partner Functions * (Select at least one)</Label>
            <div className="border border-gray-300 rounded-md p-3 max-h-48 overflow-y-auto space-y-2">
              {partnerFunctions.length === 0 ? (
                <p className="text-sm text-gray-500">No partner functions available. Create some first.</p>
              ) : (
                partnerFunctions.map(pf => (
                  <label key={pf.partner_function_id} className="flex items-center gap-2 cursor-pointer hover:bg-gray-50 p-2 rounded">
                    <input
                      type="checkbox"
                      checked={selectedPartnerFunctions.includes(pf.partner_function_id)}
                      onChange={() => togglePartnerFunction(pf.partner_function_id)}
                      className="w-4 h-4 text-orange-600 rounded focus:ring-orange-500"
                    />
                    <span className="text-sm font-medium">{pf.partner_function_id}</span>
                    <span className="text-sm text-gray-600">- {pf.name}</span>
                  </label>
                ))
              )}
            </div>
          </div>
        </>
      );
    }

    return null;
  };

  const tabs = [
    { id: 'account-groups', label: 'Account Groups' },
    { id: 'number-ranges', label: 'Number Ranges' },
    { id: 'partner-functions', label: 'Partner Functions' },
    { id: 'configurations', label: 'Account Group Config' },
  ];

  return (
    <Layout>
      <div className="p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Vendor Master Configuration</h1>
            <p className="text-sm text-gray-500 mt-1">Manage account groups and number ranges for vendor master data</p>
          </div>
          {writable && (
          <Button onClick={() => openDialog(null)}>
            <Plus className="w-4 h-4 mr-2" />
            Add New
          </Button>
          )}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 border-b border-gray-200">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => window.location.href = `/vendor-master?tab=${tab.id}`}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeTab === tab.id
                  ? 'text-orange-600 border-b-2 border-orange-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <Card className="p-6">
          {isLoading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
              <p className="mt-2 text-gray-500">Loading...</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">ID</th>
                    <th className="text-left py-3 px-4 text-sm font-semibold text-gray-700">Details</th>
                    <th className="text-right py-3 px-4 text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getCurrentData().length === 0 ? (
                    <tr>
                      <td colSpan={3} className="text-center py-8 text-gray-500">
                        No {activeTab === 'account-groups' ? 'account groups' : 'number ranges'} found. Click "Add New" to create one.
                      </td>
                    </tr>
                  ) : (
                    getCurrentData().map((item) => (
                      <tr key={item[getIdField()]} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-3 px-4 font-mono text-sm">{item[getIdField()]}</td>
                        <td className="py-3 px-4 text-sm">{getDisplayText(item)}</td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex gap-2 justify-end">
                            <RecordActions
                              doctype="vendor_master"
                              onView={() => setViewItem(item)}
                              onEdit={() => openDialog(item)}
                            />
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{getDialogTitle()}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              {getFormFields()}
              <div className="flex gap-2 justify-end pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingItem ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <RecordViewDialog
        open={!!viewItem}
        onOpenChange={(o) => { if (!o) setViewItem(null); }}
        title="Details"
        record={viewItem}
      />
    </Layout>
  );
};

export default VendorMaster;
